import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useVendorAuth } from '../../context/VendorAuthContext';
import { setSessionWithExpiry } from '../helpers/sessionUtils';
import apiClient from '../../api/apiClient';
import SEO from '../../components/common/SEO';
import Header from '../common/Header';
import './FreeTrial.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const FREE_TRIAL_RESOURCES = [
  { id: 'free-trial-fa', rel: 'stylesheet', href: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css' }
];
const SELECTED_PLAN_STORAGE_KEY = 'freeTrialSelectedPlanId';
const OTP_LENGTH = 6;
const FREE_TRIAL_DAYS = 3;

const normalizePhone = (value) => String(value || '').replace(/\D/g, '').slice(-10);
const parseFeatures = (features) => {
  if (!features) return [];
  if (Array.isArray(features)) return features.filter(Boolean);
  try {
    const parsed = JSON.parse(features);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch (error) {
    // Fall back to comma-separated parsing.
  }
  return String(features)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};
const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format(Number(value || 0));
const isStrongPassword = (password) => {
  if (!password || password.length < 8) return false;
  return /[a-z]/.test(password)
    && /[A-Z]/.test(password)
    && /\d/.test(password)
    && /[^A-Za-z0-9]/.test(password);
};
const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
};

function FreeTrial() {
  const { login } = useVendorAuth();

  const [plans, setPlans] = useState([]);
  const [isPlansLoading, setIsPlansLoading] = useState(true);
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(() => '');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [trialChargeAmount, setTrialChargeAmount] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState('mobile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [userId, setUserId] = useState(null);
  const [registrationToken, setRegistrationToken] = useState('');
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [paymentMeta, setPaymentMeta] = useState(null);

  const [phone, setPhone] = useState('');
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const otpRefs = useRef([]);
  const plansSectionRef = useRef(null);
  const plansCarouselRef = useRef(null);

  const [details, setDetails] = useState({ name: '', email: '', password: '' });
  const [emailState, setEmailState] = useState({ checking: false, available: null });

  const activePaidPlans = useMemo(() => {
    return plans
      .filter((plan) => String(plan?.status || '').toLowerCase() === 'active')
      .filter((plan) => !plan?.is_trial)
      .filter((plan) => Number(plan?.price) > 0)
      .sort((a, b) => Number(a.price) - Number(b.price));
  }, [plans]);

  const lowestPlan = activePaidPlans[0] || null;
  const planCards = activePaidPlans.slice(1);

  useEffect(() => {
    document.title = 'Free Trial';
    FREE_TRIAL_RESOURCES.forEach((resource) => {
      if (!document.getElementById(resource.id)) {
        const link = document.createElement('link');
        link.id = resource.id;
        link.rel = resource.rel;
        link.href = resource.href;
        document.head.appendChild(link);
      }
    });
    return () => {
      FREE_TRIAL_RESOURCES.forEach((resource) => {
        const existing = document.getElementById(resource.id);
        if (existing) existing.remove();
      });
    };
  }, []);

  useEffect(() => {
    if (!showPlans || !plansSectionRef.current) return;

    const animationFrame = window.requestAnimationFrame(() => {
      plansSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [showPlans]);

  useEffect(() => {
    const carousel = plansCarouselRef.current;
    const mobileViewport = window.matchMedia('(max-width: 991px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!showPlans || !carousel || !mobileViewport.matches || reducedMotion.matches || planCards.length < 2) {
      return undefined;
    }

    const autoScroll = window.setInterval(() => {
      const firstCard = carousel.querySelector('.trial-plan-card');
      if (!firstCard) return;

      const styles = window.getComputedStyle(carousel);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const step = firstCard.getBoundingClientRect().width + gap;
      const reachedEnd = carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 2;

      carousel.scrollTo({
        left: reachedEnd ? 0 : carousel.scrollLeft + step,
        behavior: 'smooth'
      });
    }, 3000);

    return () => window.clearInterval(autoScroll);
  }, [showPlans, planCards.length]);

  useEffect(() => {
    let mounted = true;
    const loadPlans = async () => {
      setIsPlansLoading(true);
      try {
        const response = await apiClient.get('/vendors/subscription/public-plans');
        if (!mounted) return;
        setPlans(Array.isArray(response.data?.plans) ? response.data.plans : []);
      } catch (error) {
        console.error('Failed to load plans:', error);
        toast.error('Failed to load plans.');
      } finally {
        if (mounted) setIsPlansLoading(false);
      }
    };

    loadPlans();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!activePaidPlans.length) return;
    const storedPlan = activePaidPlans.find((plan) => String(plan.id) === String(selectedPlanId));
    const nextPlan = storedPlan || activePaidPlans[0];
    setSelectedPlan(nextPlan);
    setSelectedPlanId(String(nextPlan.id));
    localStorage.setItem(SELECTED_PLAN_STORAGE_KEY, String(nextPlan.id));
  }, [activePaidPlans, selectedPlanId]);

  useEffect(() => {
    setTrialChargeAmount(Number(lowestPlan?.price || 0));
  }, [lowestPlan]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const timer = setTimeout(() => otpRefs.current[0]?.focus(), 100);
    return () => clearTimeout(timer);
  }, [modalOpen, modalStep]);

  const clearMessage = () => setMessage({ type: '', text: '' });

  const openPlan = (plan) => {
    const nextPlan = plan || activePaidPlans[0];
    if (!nextPlan) {
      toast.error('No active paid plans are available right now.');
      return;
    }
    const nextId = String(nextPlan.id);
    setSelectedPlan(nextPlan);
    setSelectedPlanId(nextId);
    localStorage.setItem(SELECTED_PLAN_STORAGE_KEY, nextId);
    setModalOpen(true);
    setModalStep('mobile');
    clearMessage();
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setModalOpen(false);
    setModalStep('mobile');
    setOtpDigits(Array(OTP_LENGTH).fill(''));
  };

  const startFreeTrial = async () => {
    clearMessage();

    if (!selectedPlan) {
      setMessage({ type: 'error', text: 'Please select a plan first.' });
      return;
    }

    const normalized = normalizePhone(phone);
    if (normalized.length !== 10) {
      setMessage({ type: 'error', text: 'Please enter a valid 10 digit mobile number.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/vendors/initiate-registration`, {
        phone: normalized,
        selected_plan_id: selectedPlan.id
      });

      const user = response.data?.user || response.data?.vendor || response.data?.data?.user;
      const token = response.data?.token || response.data?.data?.token;

      if (!user?.id || !token) {
        throw new Error('Unable to start registration.');
      }

      setUserId(user.id);
      setRegistrationToken(token);
      setPhone(normalized);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setModalStep('otp');
      await axios.post(`${API_BASE_URL}/vendors/${user.id}/send-otp`, {}, {
        headers: { 'registration-token': token }
      });
      setMessage({ type: 'success', text: 'OTP has been sent to your mobile number.' });
    } catch (error) {
      console.error('Free trial start failed:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || error.message || 'Failed to start free trial.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (!userId || !registrationToken || isSubmitting) return;
    setIsSubmitting(true);
    clearMessage();
    try {
      await axios.post(`${API_BASE_URL}/vendors/${userId}/send-otp`, {}, {
        headers: { 'registration-token': registrationToken }
      });
      setMessage({ type: 'success', text: 'OTP resent successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to resend OTP.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const next = [...otpDigits];
    next[index] = String(value || '').replace(/\D/g, '').slice(-1);
    setOtpDigits(next);
    if (next[index] && otpRefs.current[index + 1]) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const verifyOtp = async () => {
    clearMessage();
    const otp = otpDigits.join('');
    if (otp.length !== OTP_LENGTH) {
      alert('otp not proper');

      setMessage({ type: 'error', text: 'Please enter the complete OTP.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/vendors/${userId}/registration-details`, {
        completed_step: 2,
        user_entered_otp: otp,
        registration_token: registrationToken,
      }, {
        headers: { 'registration-token': registrationToken }
      });
      console.log(response, 'response put call');

      if (!response.data.user?.is_verified) {
        throw new Error('OTP verification failed.');
      }

      setModalStep('payment');
      setMessage({ type: 'success', text: 'OTP verified. Proceeding to payment.' });
      await startTrialPayment();
    } catch (error) {
      console.error('OTP verification failed:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || error.message || 'OTP verification failed.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startTrialPayment = async () => {
    if (!selectedPlan || !userId || !registrationToken) {
      throw new Error('Missing registration session.');
    }

    const response = await axios.post(`${API_BASE_URL}/vendors/subscription/free-trial/create-order`, {
      userId,
      registration_token: registrationToken,
      selected_plan_id: selectedPlan.id,
      trial_days: FREE_TRIAL_DAYS
    });

    const order = response.data?.order;
    if (!order?.id) {
      throw new Error('Unable to create payment order.');
    }

    setTrialChargeAmount(Number(response.data?.trialChargeAmount || lowestPlan?.price || 0));

    await loadRazorpayScript();

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || '',
      amount: order.amount,
      currency: order.currency || 'INR',
      name: 'MangoLoops',
      description: `3 day free trial for ${selectedPlan.name}`,
      order_id: order.id,
      prefill: {
        name: details.name || '',
        email: details.email || ''
      },
      notes: {
        selected_plan_id: String(selectedPlan.id),
        trial_days: String(FREE_TRIAL_DAYS)
      },
      handler: async (responseData) => {
        try {
          const verifyResponse = await axios.post(`${API_BASE_URL}/vendors/subscription/free-trial/verify-payment`, {
            userId,
            registration_token: registrationToken,
            selected_plan_id: selectedPlan.id,
            trial_days: FREE_TRIAL_DAYS,
            razorpayData: responseData
          });

          const token = verifyResponse.data?.token;
          const user = verifyResponse.data?.user;

          if (!token || !user) {
            throw new Error('Payment verification succeeded but login details were not returned.');
          }

          setSessionWithExpiry({ token, user });
          setModalOpen(false);
          toast.success('Payment verified successfully! Please complete the KYC Verification...');
          await login(token, user);
        } catch (error) {
          console.error('Payment verification failed:', error);
          setMessage({ type: 'error', text: error.response?.data?.message || 'Payment verification failed.' });
        }
      },
      modal: {
        ondismiss: () => {
          setModalStep('otp');
        }
      },
      theme: { color: '#16a34a' }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const checkEmail = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailState({ checking: false, available: null });
      return false;
    }

    setEmailState({ checking: true, available: null });
    try {
      const response = await apiClient.get(`/vendors/check-email?email=${encodeURIComponent(email)}`);
      const exists = Boolean(response.data?.exists);
      setEmailState({ checking: false, available: !exists });
      return !exists;
    } catch (error) {
      console.error('Email check failed:', error);
      setEmailState({ checking: false, available: null });
      return true;
    }
  };

  const submitDetails = async (event) => {
    event.preventDefault();
    clearMessage();

    if (!paymentVerified) {
      setMessage({ type: 'error', text: 'Please complete the payment first.' });
      return;
    }

    if (!details.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required.' });
      return;
    }

    if (!details.email.trim() || !/\S+@\S+\.\S+/.test(details.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    if (emailState.available === false) {
      setMessage({ type: 'error', text: 'This email is already registered.' });
      return;
    }

    const emailAvailable = emailState.available === true ? true : await checkEmail(details.email.trim());
    if (!emailAvailable) {
      setMessage({ type: 'error', text: 'This email is already registered.' });
      return;
    }

    if (!isStrongPassword(details.password)) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/vendors/${userId}/registration-details`, {
        completed_step: 2,
        registration_token: registrationToken,
        payment_type: 'free_trial_subscription',
        selected_plan_id: selectedPlan.id,
        transaction_id: paymentMeta?.transaction?.id || pendingPayment?.transaction?.id,
        razorpay_payment_id: paymentMeta?.razorpay_payment_id || pendingPayment?.razorpay_payment_id,
        name: details.name.trim(),
        email: details.email.trim().toLowerCase(),
        password: details.password,
        phone: normalizePhone(phone)
      }, {
        headers: { 'registration-token': registrationToken }
      });

      const token = response.data?.token;
      const user = response.data?.user;

      if (!token || !user) {
        throw new Error('Vendor creation succeeded but login details were not returned.');
      }

      setSessionWithExpiry({ token, user });
      await login(token, user);
    } catch (error) {
      console.error('Vendor creation failed:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || error.message || 'Vendor creation failed.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMessage = () => {
    if (!message.text) return null;
    return <div className={`trial-message ${message.type}`}>{message.text}</div>;
  };

  return (
    <div className="free-trial-page">
      <SEO title="Free Trial | MangoLoops" description="Start a 3 day free trial and choose a paid plan after OTP and payment verification." />

      <Header
        rightAction={(
          <button
            type="button"
            className="trial-btn orange"
            style={{ width: 'auto', padding: '0.7rem 1.15rem', borderRadius: 999 }}
            onClick={() => setModalOpen(true)}
          >
            Register
          </button>
        )}
      />

      <main className="banner-content free-trial-shell" style={{ padding: '1.75rem 0 2rem' }}>
        <section className="free-trial-hero">
          <h1 className="trial-headline">
            3 Days <span className="trial-badge trial-badge-success">FREE</span> Trial
          </h1>
          {/* <div className="trial-rating">
            <span className="stars">★★★★★</span>
            <strong style={{ color: '#111827' }}>4.9</strong>
          </div> */}

          <div className="trial-offer-box">
            <div className="trial-offer-item mb-3">
              <div className="trial-icon-box trial-icon-success">
                <i className="fa-solid fa-wallet" />
              </div>
              <div>
                <h3 className="trial-offer-title">
                  Start Trial by Paying {formatCurrency(trialChargeAmount)}
                </h3>
                <p className="trial-offer-subtitle">Refunded immediately</p>
              </div>
            </div>

            <div className="trial-offer-item mb-3">
              <div className="trial-icon-box trial-icon-success">
                <i className="fa-solid fa-unlock" />
              </div>
              <div>
                <h3 className="trial-offer-title">Trial Starts, Cancel Anytime</h3>
                <p className="trial-offer-subtitle">Receive genuine customer enquiries during your trial.</p>
              </div>
            </div>

            <div className="trial-offer-item">
              <div className="trial-icon-box trial-icon-muted">
                <i className="fa-solid fa-credit-card" />
              </div>
              <div>
                <h3 className="trial-offer-title">Auto charge after {FREE_TRIAL_DAYS} days</h3>
                <p className="trial-offer-subtitle">After 3 days, autopay every Month.</p>
              </div>
            </div>
          </div>

          <div className="trial-banner mt-4 mb-2 d-flex align-items-center justify-content-between">
            <div className="trial-banner-text">
              <span className="trial-banner-brand">Mango</span>
              <span className="trial-banner-subbrand">Loops</span>
            </div>
            <div className="trial-banner-figure">
              <img src="/favicon.ico" alt="MangoLoops" />
            </div>
          </div>

          <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1.25rem' }}>
            Get reminded before the trial ends. Cancel anytime.
          </p>

          <button
            type="button"
            className="trial-toggle-links"
            onClick={() => setShowPlans((prev) => !prev)}
            aria-expanded={showPlans}
            aria-controls="free-trial-plans"
          >
            {showPlans ? 'Hide plans and offers' : 'More plans and offers'}{' '}
            <i className={`fa-solid ${showPlans ? 'fa-chevron-up' : 'fa-chevron-down'} ms-1`} />
          </button>

          <div className="mt-3">
            <button type="button" className="trial-cta" onClick={() => openPlan(selectedPlan || lowestPlan)}>
              <span>Start FREE Trial</span>
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </section>

        {showPlans && (
          <section
            ref={plansSectionRef}
            className="trial-plans-section mt-5 pt-4"
            style={{ borderTop: '1px solid #f3f4f6' }}
            id="free-trial-plans"
          >
            <h2 className="trial-section-title mb-4">Choose Your Premium Plan</h2>

            {isPlansLoading ? (
              <div className="text-center text-secondary py-5">Loading plans...</div>
            ) : planCards.length ? (
              <>
                <div ref={plansCarouselRef} className="trial-plans-scroll trial-no-scrollbar">
                  {planCards.map((plan, index) => {
                    const featureList = parseFeatures(plan.features);
                    const isSelected = String(plan.id) === String(selectedPlan?.id || selectedPlanId);
                    return (
                      <article key={plan.id} className={`trial-plan-card ${isSelected ? 'active' : ''}`}>
                        {isSelected ? <span className="trial-plan-badge">Selected</span> : null}
                        <div>
                          <div className="trial-plan-name">{plan.name}</div>
                          <div className="trial-plan-feature">
                            {featureList.length ? featureList[0] : `${plan.leads_included || 0} leads included`}
                          </div>
                          <div className="trial-plan-price">
                            {formatCurrency(plan.price)}
                            <small>/Mon</small>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`trial-plan-button ${isSelected ? 'orange' : 'dark'}`}
                          onClick={() => openPlan(plan)}
                        >
                          Subscribe Now
                        </button>
                      </article>
                    );
                  })}
                </div>

                <div className="trial-plans-wrap d-none d-lg-grid">
                  {planCards.map((plan, index) => {
                    const featureList = parseFeatures(plan.features);
                    const isSelected = String(plan.id) === String(selectedPlan?.id || selectedPlanId);
                    const accent = index === 1 ? 'orange' : 'dark';
                    return (
                      <article key={plan.id} className={`trial-plan-card ${isSelected ? 'active' : ''}`}>
                        {index === 1 ? <span className="trial-plan-badge">Best Value</span> : null}
                        <div>
                          <div className="trial-plan-name">{plan.name}</div>
                          <div className="trial-plan-feature">
                            {featureList.length ? featureList.join(', ') : `${plan.leads_included || 0} leads included`}
                          </div>
                          <div className="trial-plan-price">
                            {formatCurrency(plan.price)}
                            <small>/Mon</small>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`trial-plan-button ${accent}`}
                          onClick={() => openPlan(plan)}
                        >
                          Subscribe Now
                        </button>
                      </article>
                    );
                  })}
                </div>
                <p className="trial-mobile-indicator d-lg-none">
                  <i className="fa-solid fa-arrows-left-right me-1" /> Swipe to see all plans
                </p>
              </>
            ) : (
              <div className="text-center text-secondary py-5">No active paid plans are available right now.</div>
            )}
          </section>
        )}

      </main>

      {modalOpen ? (
        <div className="trial-modal-backdrop" role="dialog" aria-modal="true">
          <div className={`trial-modal-card ${modalStep === 'details' ? 'details' : ''}`}>
            <button type="button" className="trial-modal-close" onClick={closeModal} aria-label="Close">
              <i className="fa-solid fa-xmark" />
            </button>

            {modalStep === 'details' ? (
              <div className="trial-details-modal">
                <div className="trial-modal-title">
                  <h3>Complete Your Vendor Profile</h3>
                  <p>Finish the registration with your name, email, and password to log in automatically.</p>
                </div>

                {renderMessage()}

                <form onSubmit={submitDetails}>
                  <div className="trial-details-grid">
                    <div>
                      <label htmlFor="trial-name">Name</label>
                      <input
                        id="trial-name"
                        type="text"
                        value={details.name}
                        onChange={(e) => setDetails((prev) => ({ ...prev, name: e.target.value }))}
                        autoComplete="name"
                      />
                    </div>

                    <div>
                      <label htmlFor="trial-email">Email</label>
                      <input
                        id="trial-email"
                        type="email"
                        value={details.email}
                        onChange={(e) => setDetails((prev) => ({ ...prev, email: e.target.value }))}
                        autoComplete="email"
                      />
                    </div>

                    <div>
                      <label htmlFor="trial-password">Password</label>
                      <input
                        id="trial-password"
                        type="password"
                        value={details.password}
                        onChange={(e) => setDetails((prev) => ({ ...prev, password: e.target.value }))}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  <div className="trial-details-footer">
                    <div className="trial-note">
                      Selected plan: <strong>{selectedPlan?.name || 'Plan selected'}</strong> · Trial charge: <strong>{formatCurrency(trialChargeAmount)}</strong>
                    </div>
                    <button type="submit" className="trial-btn green" style={{ width: 'auto', paddingInline: '1.3rem' }} disabled={isSubmitting}>
                      {isSubmitting ? (
                        <span className="trial-loading">
                          <span className="trial-spinner" /> Creating vendor...
                        </span>
                      ) : (
                        'Create Vendor & Login'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : null}

            {modalStep === 'otp' ? (
              <button
                type="button"
                className="trial-modal-back"
                onClick={() => setModalStep('mobile')}
              >
                <i className="fa-solid fa-arrow-left" /> Back
              </button>
            ) : null}

            {modalStep === 'mobile' ? (
              <div>
                <div className="trial-modal-title">
                  <img
                    className="trial-modal-logo"
                    src="https://mangoloops.com/logo-mangoloops.png"
                    alt="MangoLoops"
                  />
                  <h3>ENTER MOBILE NUMBER</h3>
                  <p>We will send an OTP to verify your number.</p>
                </div>

                {renderMessage()}

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div className="trial-input-group">
                    <span className="trial-input-prefix">+91</span>
                    <input
                      type="tel"
                      maxLength={10}
                      className="trial-input"
                      placeholder="Enter 10 digit number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(-10))}
                    />
                  </div>

                  <button
                    type="button"
                    className="trial-btn orange"
                    onClick={startFreeTrial}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Please wait...' : 'Proceed Securely'}
                  </button>
                </div>
              </div>
            ) : null}

            {modalStep === 'otp' ? (
              <div>
                <div className="trial-modal-title">
                  <h3>ENTER OTP</h3>
                  <p>Sent to your registered mobile number</p>
                </div>

                {renderMessage()}

                <div className="trial-otp-grid mb-4">
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        otpRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="trial-otp-cell"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    />
                  ))}
                </div>

                <div className="text-center mb-4">
                  <button type="button" className="trial-resend-btn" onClick={resendOtp}>
                    Resend OTP
                  </button>
                </div>

                <button
                  type="button"
                  className="trial-btn green"
                  onClick={verifyOtp}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify & Start Trial'}
                </button>
              </div>
            ) : null}

            {modalStep === 'payment' ? (
              <div>
                <div className="trial-modal-title">
                  <h3>PAYMENT READY</h3>
                  <p>Backend payment order is being prepared.</p>
                </div>
                <div className="trial-message info mb-4">
                  {selectedPlan ? (
                    <>
                      Trial charge: <strong>{formatCurrency(selectedPlan.price || lowestPlan?.price || 0)}</strong>
                      <br />
                      Selected plan: <strong>{selectedPlan.name}</strong>
                    </>
                  ) : (
                    'Preparing the payment gateway.'
                  )}
                </div>
                <button
                  type="button"
                  className="trial-btn orange"
                  onClick={async () => {
                    try {
                      await startTrialPayment();
                    } catch (error) {
                      setMessage({ type: 'error', text: error.message || 'Unable to start payment.' });
                      setModalStep('otp');
                    }
                  }}
                >
                  Open Payment
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default FreeTrial;
